// Which of a room's pool blobs have an MP3 preview — asked of the API in one
// request, not discovered one 404 at a time.
//
// A waveform is drawn from a blob's preview (~100 KB) where it has one, and
// from the object itself (megabytes) where it has not. The room used to find
// out by requesting the preview and reading the 404: one wasted round trip
// per preview-less blob per visit, and blobs uploaded before the extension
// encoded previews are most of the older seeds (#125). This asks once.

import { POOL_PREVIEWS_MAX, type PoolPreviewsRequest, type PoolPreviewsResponse } from "@shared/api-types.js";
import { poolHashAnyChannel } from "@shared/blobs.js";
import { json, webApi } from "@web-shared/lib/web-api";

/** The distinct pool hashes these clips draw from, in first-seen order. A
 * legacy per-seed key, a local take or a MIDI clip names no pool blob. */
export function poolHashesOf(clips: ReadonlyArray<{ audioKey: string | null }>): string[] {
  const out = new Set<string>();
  for (const clip of clips) {
    if (!clip.audioKey) continue;
    const hash = poolHashAnyChannel(clip.audioKey);
    if (hash !== null) out.add(hash);
  }
  return [...out];
}

/** Which of these hashes have a preview. Chunked to the API's cap; the
 * chunks run together. Throws if any chunk fails — the caller decides what
 * not knowing means. */
export async function lookupPoolPreviews(hashes: readonly string[]): Promise<Set<string>> {
  const chunks: string[][] = [];
  for (let i = 0; i < hashes.length; i += POOL_PREVIEWS_MAX) chunks.push(hashes.slice(i, i + POOL_PREVIEWS_MAX));
  const answers = await Promise.all(
    chunks.map((chunk) => {
      const body: PoolPreviewsRequest = { hashes: chunk };
      return webApi<PoolPreviewsResponse>("/audio/previews", json("POST", body));
    }),
  );
  return new Set(answers.flatMap((a) => a.hashes));
}
